/**
 * EL RELOJ DE LOS CLIENTES, EN SOMBRA (plan «La app como reloj de los clientes», fase 5, 30-09).
 *
 * La app lleva su propio reloj de semanas (core/reloj) al lado del de ActiveCampaign. Aquí se
 * ve el parte diario que los compara cliente por cliente, se pone el reloj a quien no lo tiene
 * (el arranque) y se apuntan los pagos que no pasan por Stripe.
 *
 * QUIÉN MANDA LO DICE EL BACKEND (05-10, `modo` en GET /admin/reloj): en producción el reloj va
 * en sombra y nada de esto cambia lo que ve el cliente; donde el interruptor está encendido
 * (DEV), la semana que ve el cliente sale de este reloj y se puede fijar a mano.
 *
 * SI ACTIVECAMPAIGN ESTÁ ENCENDIDO TAMBIÉN LO DICE EL BACKEND (13-10, `modo.ac_con_clave`, la
 * misma pregunta que se hace todo el servidor: `core/semana_ac.configurado`). Apagado, aquí no
 * se enseña ni la comparación con AC, ni su columna, ni sus textos (`conAC` más abajo y
 * `lib/filasDelReloj.filasSegunAC`). Encendido (producción hoy), todo como estaba.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Clock, RefreshCw, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { PlanBadge } from './ClientDashboard';
import { agruparCambios, avisoAlFijarLaSemana, cambioEnClaro, detallesEnLinea, fechaDelReloj, fraseDelAplazamiento } from '../lib/cambiosDelReloj';
import { useConfirm } from '../components/ui/confirm';
import { estadoDelProceso } from '../lib/estadoDelProceso';
import { mensajeDeError } from '../lib/mensajeDeError';
import { cuentaDelReloj, filasDelReloj, filasSegunAC, filtrarFilas, filtroDeEntrada, filtrosDelReloj } from '../lib/filasDelReloj';
import { ahoraDeLaApp } from '../lib/horaEspana';

// Qué hace este servidor con el reloj (lib/estadoDelProceso): parado va en color de aviso.
const TONO_DEL_PROCESO = {
    normal: 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10',
    apagado: 'text-white/70 border-[#333] bg-white/5',
    aviso: 'text-amber-300 border-amber-500/30 bg-amber-500/10',
};

const RESULTADO = {
    coincide: { texto: 'Coincide', clase: 'bg-emerald-500/15 text-emerald-400' },
    difiere: { texto: 'Difiere', clase: 'bg-red-500/15 text-red-400' },
    sin_ac: { texto: 'Sin AC', clase: 'bg-amber-500/15 text-amber-300' },
    sin_semanas: { texto: 'Por meses', clase: 'bg-white/10 text-white/60' },
    // Con ActiveCampaign apagado no se compara: se dice si el reloj le cuenta una semana.
    con_semana: { texto: 'Con semana', clase: 'bg-emerald-500/15 text-emerald-400' },
    sin_semana: { texto: 'Sin semana', clase: 'bg-amber-500/15 text-amber-300' },
};
const PROGRAMA = { bloque: 'Ciclos de 4 semanas', premium: 'Premium', mensual: 'Por meses', fijo: 'Fijo', ninguno: 'Sin reloj' };

const fecha = (iso) => (iso ? new Date(iso).toLocaleString('es-ES', {
    timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
// De dónde sale hoy la semana del bloque que ve el cliente (core/semana_vigente).
const FUENTE = { reloj: 'el reloj', activecampaign: 'ActiveCampaign' };
const fuente = (v) => FUENTE[v] || 'la app (su rutina o su arranque)';
// Quién manda en este entorno, con las palabras de la cabecera.
const MODO = {
    reloj: {
        chapa: 'Manda el reloj: lo que cambies aquí lo ve el cliente', clase: 'text-[#FF671F] border-[#FF671F]/40 bg-[#FF671F]/10' },
    activecampaign: {
        chapa: 'En sombra: no cambia nada de lo que ve el cliente', clase: 'text-sky-300 border-sky-500/30 bg-sky-500/10' },
    // Aquí ActiveCampaign está apagado, así que ni se le nombra.
    app: {
        chapa: 'En sombra: no cambia nada de lo que ve el cliente', clase: 'text-sky-300 border-sky-500/30 bg-sky-500/10' },
};
const semana = (v) => (v === null || v === undefined ? 'sin semana' : `semana ${v}`);
// «Ciclo 2 de 3»: la vuelta de 4 semanas (Francisco, 30-09: «que se llame ciclo»).
const ciclo = (c, total) => (c ? `Ciclo ${c}${total ? ` de ${total}` : ''}` : null);

const Cifra = ({ n, t, clase = 'text-white' }) => (
    <div className="bg-[#0E0E0E] border border-[#1E1E1E] rounded-lg p-3">
        <p className={`text-2xl font-bold ${clase}`} style={{ fontFamily: 'Barlow Condensed' }}>{n}</p>
        <p className="text-xs text-white/45">{t}</p>
    </div>
);

const AdminRelojPage = () => {
    const { api } = useAuth();
    const [datos, setDatos] = useState(null);
    // El filtro que eligió quien mira; mientras no elija, el de entrada de este entorno.
    const [filtroElegido, setFiltro] = useState(null);
    const [buscar, setBuscar] = useState('');
    const [trabajando, setTrabajando] = useState(false);
    const [ensayo, setEnsayo] = useState(null);
    const [abierto, setAbierto] = useState(null);

    const cargar = () => api.get('/admin/reloj')
        .then((r) => setDatos(r.data))
        .catch((e) => { console.error('[reloj]', e); toast.error('No se pudo cargar el reloj'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al entrar
    useEffect(() => { cargar(); }, []);

    const parte = datos?.parte;
    const modo = datos?.modo || {};
    // ¿Está ActiveCampaign encendido en este servidor? Lo dice él. Un servidor que aún no
    // lo diga se trata como hasta ahora (encendido).
    const conAC = modo.ac_con_clave !== false;
    const m = MODO[modo.quien_manda] || (conAC ? MODO.activecampaign : MODO.app);
    const filtros = filtrosDelReloj(conAC);
    const filtro = filtros.some(([k]) => k === filtroElegido) ? filtroElegido : filtroDeEntrada(conAC);
    const proceso = estadoDelProceso(datos?.proceso);
    // El parte guardado y, detrás, quien tiene reloj desde después (un alta de hoy): los
    // números y la lista salen de lo mismo (lib/filasDelReloj).
    const todas = useMemo(() => filasSegunAC(filasDelReloj(parte, datos?.desde_el_parte), conAC), [parte, datos, conAC]);
    const cuenta = useMemo(() => cuentaDelReloj(todas), [todas]);
    const filas = useMemo(() => filtrarFilas(todas, filtro, buscar), [todas, filtro, buscar]);

    const hacerParte = async () => {
        setTrabajando(true);
        try {
            await api.post('/admin/reloj/parte');
            await cargar();
            toast.success('Parte hecho');
        } catch (e) {
            console.error('[reloj]', e);
            toast.error('No se pudo hacer el parte');
        } finally {
            setTrabajando(false);
        }
    };

    const arrancar = async (escribir) => {
        const aviso = modo.reloj_manda
            ? 'Se va a poner el reloj a los clientes activos que no lo tienen. Aquí manda el reloj: su semana pasa a salir de él. ¿Seguimos?'
            : 'Se va a poner el reloj a los clientes activos que no lo tienen. Solo escribe en el reloj, en sombra. ¿Seguimos?';
        if (escribir && !window.confirm(aviso)) return;
        setTrabajando(true);
        try {
            const r = await api.post('/admin/reloj/arrancar', { escribir });
            setEnsayo(r.data);
            if (escribir) {
                await api.post('/admin/reloj/parte');
                await cargar();
                toast.success(`Reloj puesto a ${r.data.puestos} clientes`);
            }
        } catch (e) {
            console.error('[reloj]', e);
            toast.error('No se pudo preparar el arranque');
        } finally {
            setTrabajando(false);
        }
    };

    if (!datos) return <div className="p-6 text-white/50">Cargando el reloj…</div>;

    return (
        <div className="p-4 sm:p-6 space-y-5 max-w-[1400px]" data-testid="reloj">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <h1 className="text-3xl sm:text-4xl font-bold text-white uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
                    Reloj de los clientes
                </h1>
                <div className="flex flex-col items-start gap-2 min-w-0 max-w-full">
                    <span data-testid="reloj-modo" className={`inline-flex items-center gap-1.5 text-xs border rounded-full px-3 py-1 ${m.clase}`}>
                        <Clock className="w-3.5 h-3.5" /> {m.chapa}
                    </span>
                    {proceso && (
                        <span data-testid="reloj-proceso" data-tono={proceso.tono}
                            className={`inline-flex items-start gap-1.5 text-xs border rounded-2xl px-3 py-1 max-w-full ${TONO_DEL_PROCESO[proceso.tono]}`}>
                            <Clock className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span className="min-w-0 break-words">{proceso.texto}</span>
                        </span>
                    )}
                    {proceso?.detalle && (
                        <p data-testid="reloj-proceso-detalle" className="text-[11px] text-amber-300/70 max-w-sm break-words">{proceso.detalle}</p>
                    )}
                </div>
            </div>

            <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${conAC ? 'lg:grid-cols-6' : 'lg:grid-cols-5'}`} data-testid="reloj-cifras">
                <Cifra n={`${datos.relojes} / ${datos.activos}`} t="con reloj / activos" />
                {conAC ? (
                    <>
                        <Cifra n={cuenta.coincide || 0} t="coinciden con AC" clase="text-emerald-400" />
                        <Cifra n={cuenta.difiere || 0} t="difieren de AC" clase="text-red-400" />
                        <Cifra n={cuenta.sin_ac || 0} t="sin dato de AC" clase="text-amber-300" />
                    </>
                ) : (
                    <>
                        <Cifra n={cuenta.con_semana || 0} t="con semana" clase="text-emerald-400" />
                        <Cifra n={cuenta.sin_semana || 0} t="sin semana" clase="text-amber-300" />
                    </>
                )}
                <Cifra n={cuenta.sin_semanas || 0} t="por meses" clase="text-white/70" />
                <div className="bg-[#0E0E0E] border border-[#1E1E1E] rounded-lg p-3 flex flex-col justify-between gap-2">
                    <p className="text-xs text-white/45">{parte ? `Parte del ${fecha(parte.hecho)}` : 'Aún no hay parte'}</p>
                    <Button onClick={hacerParte} disabled={trabajando} data-testid="reloj-parte"
                        className="h-8 w-full bg-[#1F1F1F] hover:bg-[#2A2A2A] text-white text-xs px-2">
                        <RefreshCw className="w-3.5 h-3.5 mr-1.5 shrink-0" />Hacer el parte
                    </Button>
                </div>
            </div>

            {(datos.relojes < datos.activos) && (
                <Card className="bg-[#111] border-[#FF671F]/40">
                    <CardContent className="p-4 sm:p-5 space-y-3">
                        <p className="text-white font-medium">
                            {datos.relojes === 0 ? 'Todavía no hay relojes puestos.' : `A ${datos.activos - datos.relojes} clientes activos les falta el reloj.`}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <Button onClick={() => arrancar(false)} disabled={trabajando} data-testid="reloj-ensayo"
                                className="bg-[#1F1F1F] hover:bg-[#2A2A2A] text-white">Ensayar el arranque</Button>
                            {ensayo && !ensayo.escrito && (
                                <Button onClick={() => arrancar(true)} disabled={trabajando} data-testid="reloj-arrancar"
                                    className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white font-bold">
                                    Poner el reloj a {ensayo.puestos}
                                </Button>
                            )}
                        </div>
                        {ensayo && (
                            <div className="text-sm text-white/70 space-y-1" data-testid="reloj-ensayo-parte">
                                <p>{ensayo.escrito ? 'Puesto' : 'Se pondría'} a {ensayo.puestos} clientes
                                    ({Object.entries(ensayo.por_programa || {}).map(([k, v]) => `${v} ${(PROGRAMA[k] || k).toLowerCase()}`).join(', ')}).
                                    {ensayo.ya_tenian ? ` ${ensayo.ya_tenian} ya lo tenían.` : ''}</p>
                                {ensayo.sin_ac?.length > 0 && (
                                    <p className="text-amber-300/90" data-testid="reloj-ensayo-sin-semana">{ensayo.escrito ? 'Se han quedado' : 'Se quedarían'} SIN SEMANA {ensayo.sin_ac.length} de ciclos de 4 semanas y Premium{conAC ? ' (sin dato reciente de ActiveCampaign)' : ''}: {ensayo.sin_ac.slice(0, 12).join(', ')}{ensayo.sin_ac.length > 12 ? '…' : ''}</p>
                                )}
                                {ensayo.sin_plan?.length > 0 && (
                                    <p className="text-red-300/90">Con un plan que el catálogo no conoce ({ensayo.sin_plan.length}), sin reloj: {ensayo.sin_plan.join(', ')}</p>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {parte && (
                <Card className="bg-[#111] border-[#222]">
                    <CardContent className="p-4 sm:p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                            <div className="flex flex-wrap gap-1.5">
                                {filtros.map(([k, t]) => (
                                    <button key={k} type="button" onClick={() => setFiltro(k)}
                                        className={`text-sm px-3 py-1.5 rounded-lg ${filtro === k ? 'bg-[#FF671F] text-white' : 'bg-[#1A1A1A] text-white/60 hover:text-white'}`}>
                                        {t} ({cuenta[k] || 0})
                                    </button>
                                ))}
                            </div>
                            <div className="relative w-full sm:max-w-[260px]">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                                <input value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Buscar cliente…"
                                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF671F]" />
                            </div>
                        </div>
                        {filas.length === 0 ? (
                            <p className="text-white/50 text-sm">No hay clientes en esta lista.</p>
                        ) : (
                            <div className="space-y-2" data-testid="reloj-filas">
                                {filas.map((f) => (
                                    <Fila key={f.client_id} f={f} abierto={abierto === f.client_id}
                                        alternar={() => setAbierto(abierto === f.client_id ? null : f.client_id)}
                                        medios={datos.medios} alGuardar={cargar}
                                        sale={(datos.fuentes || {})[f.client_id]} puedeFijar={!!modo.puede_fijar} conAC={conAC} />
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

const Fila = ({ f, abierto, alternar, medios, alGuardar, sale, puedeFijar, conAC }) => {
    const r = RESULTADO[f.resultado] || RESULTADO.sin_semanas;
    return (
        <div className={`rounded-lg border ${abierto ? 'border-[#FF671F]/50' : 'border-[#1E1E1E]'} bg-[#0E0E0E]`}>
            <button type="button" onClick={alternar} className="w-full text-left p-3 grid grid-cols-1 md:grid-cols-[1.6fr_0.9fr_1.2fr_1fr_2fr_auto] gap-2 md:gap-3 md:items-center">
                <div>
                    <p className="text-white font-medium">{f.nombre || f.email}</p>
                    <p className="text-xs text-white/40 break-all">{f.email}</p>
                    {/* Tiene reloj desde después del parte que se está enseñando (un alta de hoy). */}
                    {f.desde_el_parte && (
                        <p className="text-[11px] text-[#FF671F]/90 mt-0.5" data-testid="reloj-desde-el-parte">Desde el último parte</p>
                    )}
                </div>
                <div className="flex items-center gap-2"><PlanBadge plan={f.plan} /></div>
                <div className="text-sm">
                    <p className="text-white/85">App: {ciclo(f.ciclo, f.ciclos) ? `${ciclo(f.ciclo, f.ciclos)}, ` : ''}{semana(f.app)}</p>
                    {conAC && <p className="text-white/45">AC: {semana(f.ac)}</p>}
                    <p className="text-xs text-white/40" data-testid="reloj-fuente">Su semana sale de: {fuente(sale)}</p>
                </div>
                <div className="text-sm text-white/60">
                    {f.semana_plan != null ? `Plan: semana ${f.semana_plan}` : 'Plan sin inicio'}
                    {f.reporte_mensual && <p className="text-xs text-white/40">Mensual: {f.reporte_mensual.toLowerCase()}</p>}
                    {f.mensual_aplazado_hasta && <p className="text-xs text-sky-300/80" data-testid="reloj-fila-aplazo">Aplazó su mensual</p>}
                </div>
                <p className="text-xs text-white/55">{f.motivo || PROGRAMA[f.programa] || ''}</p>
                <div className="flex items-center gap-2 md:justify-end">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.clase}`}>{r.texto}</span>
                    {abierto ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                </div>
            </button>
            {abierto && <Detalle clientId={f.client_id} medios={medios} alGuardar={alGuardar} puedeFijar={puedeFijar} />}
        </div>
    );
};

const hoyISO = () => ahoraDeLaApp().toLocaleDateString('en-CA');

const Detalle = ({ clientId, medios, alGuardar, puedeFijar }) => {
    const { api } = useAuth();
    const [d, setD] = useState(null);
    const [fijar, setFijar] = useState({ semana: '', num_semanas: '', motivo: '' });
    // EL MEDIO NACE SIN ELEGIR (12-10): nacía en "Efectivo" y un pago por Bizum apuntado
    // con prisa quedaba como efectivo sin que nadie lo hubiera dicho. Hay que escogerlo.
    const PAGO_VACIO = { fecha: hoyISO(), siguiente: '', medio: '', renueva: true };
    const [pago, setPago] = useState(PAGO_VACIO);
    const [guardando, setGuardando] = useState(false);
    const { confirm } = useConfirm();

    const cargar = () => api.get(`/admin/reloj/cliente/${clientId}`)
        .then((r) => setD(r.data))
        .catch((e) => { console.error('[reloj]', e); toast.error('No se pudo cargar su reloj'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- al abrir
    useEffect(() => { cargar(); }, [clientId]);

    // Por qué no se puede apuntar con lo que hay escrito (o null si se puede). Las mismas
    // reglas las comprueba el servidor (`core/reloj.pago_a_mano_no_vale`): el panel aceptó un
    // pago de 2030 que cubría hasta 2020. `hoyISO` es el día del navegador, el del servidor
    // es el de España: si no coinciden, manda el servidor y su frase sale en el aviso.
    const pagoNoVale = !pago.medio ? 'Elige cómo pagó.'
        : !pago.fecha ? 'Pon la fecha del pago.'
            : pago.fecha > hoyISO() ? 'La fecha del pago no puede ser posterior a hoy.'
                : (pago.siguiente && pago.siguiente < pago.fecha)
                    ? 'La fecha hasta la que cubre no puede ser anterior a la del pago.'
                    : null;

    const apuntar = async () => {
        if (pagoNoVale) { toast.error(pagoNoVale); return; }
        // A UN CLIENTE DE BAJA NO SE LE APUNTA UN PAGO SIN QUERER (12-10): se pregunta antes.
        if (d?.de_baja && !(await confirm({
            title: 'Este cliente está de baja',
            description: 'Apuntarle un pago no le devuelve el acceso: solo queda en su reloj. ¿Quieres apuntarlo igualmente?',
            confirmLabel: 'Apuntar el pago',
        }))) return;
        setGuardando(true);
        try {
            await api.post(`/admin/reloj/cliente/${clientId}/pago`, {
                ...pago, siguiente: pago.siguiente || null, ...(d?.de_baja ? { confirmar_baja: true } : {}),
            });
            toast.success('Pago apuntado en su reloj');
            // El formulario se vacía: se quedaban las fechas puestas y el mismo pago se
            // apuntaba dos veces con otro clic.
            setPago(PAGO_VACIO);
            await cargar();
            alGuardar && alGuardar();
        } catch (e) {
            console.error('[reloj]', e);
            // Las frases de esta ruta son fijas y humanas (fechas que no valen, cliente de
            // baja): se enseñan. Cualquier otra cosa, la de siempre.
            toast.error(mensajeDeError(e, 'No se pudo apuntar el pago'));
        } finally {
            setGuardando(false);
        }
    };

    // DESHACER EL ÚLTIMO PAGO APUNTADO A MANO (12-10). Cuándo se puede lo decide el servidor
    // (`core/pago_a_mano`): solo el último, y solo si su reloj sigue como lo dejó ese pago.
    // Se manda el id del pago que se está viendo: dos pulsaciones deshacen una vez.
    const deshacerPago = async () => {
        const p = d?.pago_que_se_puede_deshacer;
        if (!p?.se_puede || guardando) return;
        if (!(await confirm({
            title: 'Deshacer este pago',
            description: `Se quita de su reloj el pago del ${fechaDelReloj(p.fecha) || p.fecha} (${((medios || {})[p.medio] || p.medio || '').toLowerCase()}) y el reloj vuelve a como estaba antes de apuntarlo. Queda anotado en el historial quién lo deshizo.`,
            confirmLabel: 'Deshacer el pago', danger: true,
        }))) return;
        setGuardando(true);
        try {
            const r = await api.post(`/admin/reloj/cliente/${clientId}/pago/deshacer`, { pago_id: p.id });
            toast.success(r.data?.ya_estaba ? 'Ese pago ya estaba deshecho' : 'Pago deshecho: su reloj vuelve a como estaba');
            await cargar();
            alGuardar && alGuardar();
        } catch (e) {
            console.error('[reloj]', e);
            // Frases fijas de la ruta (el reloj se ha movido, ya no es el último): se enseñan.
            toast.error(mensajeDeError(e, 'No se pudo deshacer el pago'));
            await cargar();
        } finally {
            setGuardando(false);
        }
    };

    const fijarSemana = async () => {
        // Si tiene el mensual pedido y sin responder, cambiarle la semana se lo borra: se
        // dice antes de hacerlo (12-10, lib/cambiosDelReloj).
        const aviso = avisoAlFijarLaSemana(d?.reloj, Number(fijar.semana));
        if (aviso && !await confirm({
            title: '¿Fijar la semana igualmente?', description: aviso,
            confirmLabel: 'Fijar la semana', danger: true,
        })) return;
        setGuardando(true);
        try {
            await api.post(`/admin/reloj/cliente/${clientId}/fijar`, {
                semana: Number(fijar.semana),
                num_semanas: fijar.num_semanas === '' ? null : Number(fijar.num_semanas),
                motivo: fijar.motivo,
            });
            toast.success('Semana fijada');
            setFijar({ semana: '', num_semanas: '', motivo: '' });
            await cargar();
            // La fila de la lista sale del parte: se rehace para que no enseñe la semana vieja.
            await api.post('/admin/reloj/parte').catch((e) => console.error('[reloj]', e));
            alGuardar && alGuardar();
        } catch (e) {
            console.error('[reloj]', e);
            toast.error('No se pudo fijar la semana. Inténtalo de nuevo.');
        } finally {
            setGuardando(false);
        }
    };

    if (!d) return <p className="px-3 pb-3 text-sm text-white/40">Cargando…</p>;
    const res = d.resumen || {};
    const conSemanas = ['bloque', 'premium'].includes(res.programa);
    const tope = res.programa === 'premium' ? Number(fijar.num_semanas || res.num_semanas || 4) : 4;
    return (
        <div className="px-3 pb-4 grid lg:grid-cols-2 gap-4 border-t border-[#1E1E1E] pt-3">
            <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><span className="text-white/40">Programa:</span> <span className="text-white">{PROGRAMA[res.programa] || 'sin reloj'}</span></p>
                    <p><span className="text-white/40">Ciclo:</span> <span className="text-white">{res.ciclo ? `${res.ciclo}${res.ciclos ? ` de ${res.ciclos}` : ''}` : 'sin ciclo'}{res.semana != null ? `, semana ${res.semana} de ${res.num_semanas || 4}` : ''}</span></p>
                    <p><span className="text-white/40">Semana del plan:</span> <span className="text-white">{res.semana_plan ?? 'sin inicio'}{res.plan_semanas ? ` de ${res.plan_semanas}` : ''}</span></p>
                    <p><span className="text-white/40">Estado:</span> <span className={res.vencido ? 'text-red-400' : 'text-white'}>{res.vencido ? 'Plan vencido' : res.stop ? 'Parado (pausa o baja)' : 'En marcha'}</span></p>
                    <p><span className="text-white/40">Último pago:</span> <span className="text-white">{d.reloj?.ultimo_pago || 'sin dato'}</span></p>
                    <p><span className="text-white/40">Siguiente pago:</span> <span className="text-white">{d.reloj?.siguiente_pago || 'sin dato'}</span></p>
                    <p className="col-span-2"><span className="text-white/40">La semana que ve sale de:</span> <span className="text-white">{fuente(d.semana_de)}</span></p>
                    {/* Aplazó su mensual: el estado solo dice que no está enviado (12-10). */}
                    {fraseDelAplazamiento(d.mensual_aplazado_hasta) && (
                        <p className="col-span-2 text-sky-300" data-testid="reloj-aplazo">{fraseDelAplazamiento(d.mensual_aplazado_hasta)}</p>
                    )}
                </div>
                {puedeFijar && conSemanas && (
                    <div className="bg-[#111] border border-[#222] rounded-lg p-3 space-y-2" data-testid="reloj-fijar">
                        <p className="text-sm text-white font-medium">Fijar la semana a mano</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <label className="text-xs text-white/50">Semana del ciclo
                                <select value={fijar.semana} onChange={(e) => setFijar({ ...fijar, semana: e.target.value })}
                                    data-testid="reloj-fijar-semana"
                                    className="mt-1 w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1.5 text-sm text-white">
                                    <option value="">Elige</option>
                                    {Array.from({ length: tope + 1 }, (_, i) => <option key={i} value={i}>Semana {i}</option>)}
                                </select>
                            </label>
                            {res.programa === 'premium' && (
                                <label className="text-xs text-white/50">Semanas del ciclo
                                    <select value={fijar.num_semanas} onChange={(e) => setFijar({ ...fijar, num_semanas: e.target.value })}
                                        className="mt-1 w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1.5 text-sm text-white">
                                        <option value="">Como está ({res.num_semanas || 4})</option>
                                        {[4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </label>
                            )}
                            <label className="text-xs text-white/50 sm:col-span-2">Motivo
                                <input value={fijar.motivo} onChange={(e) => setFijar({ ...fijar, motivo: e.target.value })}
                                    data-testid="reloj-fijar-motivo" maxLength={300}
                                    className="mt-1 w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1.5 text-sm text-white" />
                            </label>
                        </div>
                        <Button onClick={fijarSemana} disabled={guardando || fijar.semana === '' || !fijar.motivo.trim()}
                            data-testid="reloj-fijar-boton"
                            className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white h-9">Fijar la semana</Button>
                    </div>
                )}
                <div className="bg-[#111] border border-[#222] rounded-lg p-3 space-y-2">
                    <p className="text-sm text-white font-medium">Apuntar un pago fuera de Stripe</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <label className="text-xs text-white/50">Fecha del pago
                            <input type="date" value={pago.fecha} max={hoyISO()} onChange={(e) => setPago({ ...pago, fecha: e.target.value })}
                                className="mt-1 w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1.5 text-sm text-white" />
                        </label>
                        <label className="text-xs text-white/50">Cubre hasta
                            <input type="date" value={pago.siguiente} min={pago.fecha || undefined} onChange={(e) => setPago({ ...pago, siguiente: e.target.value })}
                                className="mt-1 w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1.5 text-sm text-white" />
                        </label>
                        <label className="text-xs text-white/50">Cómo pagó
                            <select value={pago.medio} onChange={(e) => setPago({ ...pago, medio: e.target.value })}
                                className="mt-1 w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-md px-2 py-1.5 text-sm text-white">
                                <option value="" disabled>Elige cómo pagó</option>
                                {Object.entries(medios || {}).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
                            </select>
                        </label>
                        <label className="text-xs text-white/60 flex items-center gap-2 sm:mt-5">
                            <input type="checkbox" className="accent-[#FF671F]" checked={pago.renueva}
                                onChange={(e) => setPago({ ...pago, renueva: e.target.checked })} />
                            Es una renovación
                        </label>
                    </div>
                    {d.de_baja && (
                        <p className="text-xs text-amber-400" data-testid="reloj-pago-de-baja">
                            Este cliente está de baja.
                        </p>
                    )}
                    <Button onClick={apuntar} disabled={guardando} data-testid="reloj-apuntar-pago"
                        className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white h-9">Apuntar el pago</Button>
                    {d.pago_que_se_puede_deshacer && (
                        <div className="border-t border-[#222] pt-2 space-y-1.5" data-testid="reloj-deshacer-pago">
                            <p className="text-xs text-white/60">
                                Último pago apuntado a mano: {fechaDelReloj(d.pago_que_se_puede_deshacer.fecha) || d.pago_que_se_puede_deshacer.fecha}
                                {' · '}{((medios || {})[d.pago_que_se_puede_deshacer.medio] || d.pago_que_se_puede_deshacer.medio || '').toLowerCase()}
                                {d.pago_que_se_puede_deshacer.quien ? ` · lo apuntó ${d.pago_que_se_puede_deshacer.quien}` : ''}
                            </p>
                            {d.pago_que_se_puede_deshacer.se_puede ? (
                                <Button onClick={deshacerPago} disabled={guardando} variant="outline" data-testid="reloj-deshacer-pago-boton"
                                    className="h-8 text-xs border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200">Deshacer este pago</Button>
                            ) : (
                                <p className="text-[11px] text-amber-400/80" data-testid="reloj-deshacer-pago-no">{d.pago_que_se_puede_deshacer.por_que_no}</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div>
                <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Historial del reloj</p>
                {d.historial.length === 0 ? <p className="text-sm text-white/40">Sin cambios todavía.</p> : (
                    <ul className="space-y-1.5 max-h-[320px] overflow-auto pr-1">
                        {/* Una línea por regla del catálogo, no una por campo (lib/cambiosDelReloj). */}
                        {agruparCambios(d.historial).map((g, i) => {
                            const h = g.cambio;
                            return (
                                <li key={i} className="text-xs">
                                    <span className="text-white/40">{fecha(g.cuando)}</span>{' · '}
                                    <span className="text-white/80">{g.titulo}</span>
                                    {h ? (
                                        <span className="text-white/35">{' · '}{cambioEnClaro(h)} ({h.origen})</span>
                                    ) : (
                                        <span className="text-white/35">{g.detalles.length > 0 && <>{' · '}{detallesEnLinea(g)}</>}{g.origen ? ` (${g.origen})` : ''}</span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default AdminRelojPage;
